// Small shared helpers for the operations work-queue pages.
type Tone = 'neutral' | 'info' | 'positive' | 'warning' | 'negative' | 'accent';

const DAY_LABEL: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };

export function formatDayLabel(iso: string | null): string {
  if (!iso) return 'Unscheduled';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d!).toLocaleDateString('en-MU', DAY_LABEL);
}

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── UTC day helpers (the backend stores delivery/production dates as UTC days,
// so the calendar does all its grid math in UTC to keep cells aligned). ───────
export function isoDayUTC(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function todayIsoUTC(): string {
  return isoDayUTC(new Date());
}

export function parseIsoUTC(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!));
}

export function addDaysUTC(d: Date, n: number): Date {
  const x = new Date(d);
  x.setUTCDate(x.getUTCDate() + n);
  return x;
}

/** Monday at or before `d` (UTC). */
export function startOfWeekUTC(d: Date): Date {
  const offset = (d.getUTCDay() + 6) % 7; // 0 = Monday
  return addDaysUTC(d, -offset);
}

/** The 7 days (Mon→Sun, UTC) of the week containing `anchor`. */
export function weekDaysUTC(anchor: Date): { date: Date; iso: string }[] {
  const monday = startOfWeekUTC(anchor);
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDaysUTC(monday, i);
    return { date, iso: isoDayUTC(date) };
  });
}

/**
 * A 6-week (42-cell) Monday-start grid for the month containing `anchor` (UTC).
 * `inMonth` flags cells that belong to the anchor's month vs. spill-over days.
 */
export function monthGridUTC(anchor: Date): { date: Date; iso: string; inMonth: boolean }[] {
  const monthIndex = anchor.getUTCMonth();
  const first = new Date(Date.UTC(anchor.getUTCFullYear(), monthIndex, 1));
  const monday = startOfWeekUTC(first);
  return Array.from({ length: 42 }, (_, i) => {
    const date = addDaysUTC(monday, i);
    return { date, iso: isoDayUTC(date), inMonth: date.getUTCMonth() === monthIndex };
  });
}

export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Group rows by their day key (yyyy-mm-dd or null), sorted ascending with the
// unscheduled bucket last.
export function groupByDay<T>(rows: T[], getDay: (r: T) => string | null): { day: string | null; rows: T[] }[] {
  const map = new Map<string, T[]>();
  for (const r of rows) {
    const k = getDay(r) ?? '';
    (map.get(k) ?? map.set(k, []).get(k)!).push(r);
  }
  return [...map.entries()]
    .sort((a, b) => {
      if (a[0] === '') return 1;
      if (b[0] === '') return -1;
      return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0;
    })
    .map(([day, list]) => ({ day: day || null, rows: list }));
}

// Tone for a FulfillmentTask status pill.
export function taskTone(status: string): Tone {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'SENT':
      return 'info';
    case 'READY':
      return 'positive';
    case 'CANCELLED':
      return 'negative';
    default:
      return 'neutral';
  }
}

// Human label for a task status, per flow.
export function taskStatusLabel(status: string, flow: 'MANUFACTURE' | 'SUPPLIER'): string {
  if (status === 'PENDING') return flow === 'SUPPLIER' ? 'To send' : 'To produce';
  if (status === 'SENT') return 'Sent';
  if (status === 'READY') return flow === 'SUPPLIER' ? 'Received' : 'Produced';
  if (status === 'CANCELLED') return 'Cancelled';
  return status;
}
